//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 03/02/2025
// Version       : 1
// Description   : Muestra la patente y las observaciones en Accordions con cada parte del vehículo
//
//------Changes ----------------------------------------------------------------------------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid";

//ADJUNTAR ARCHIVOS
import FileUploadTareas from "../FileUploadTareas";

// ICONOS
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BottomDrawer from "./BottomDrawer";

function ObservacionesPanelMantenimiento({ vehiculo, handleClose }) {
  const isMobile = useMediaQuery("(max-width:600px)");

  const [openCloseAdj, setOpenCloseAdj] = useState(false); // Estado para abrir/cerrar adjuntos
  const [currentUuid, setCurrentUuid] = useState(null); // UUID Actual

  const [observaciones, setObservaciones] = useState([]);

  const obtenerObservaciones = () => {
    if (!vehiculo?.fecha_ultima_revision) return;

    const fecha = new Date(vehiculo.fecha_ultima_revision);

    // Validar que la fecha sea válida
    if (isNaN(fecha.getTime())) {
      console.error("Fecha inválida:", vehiculo.fecha_ultima_revision);
      return;
    }

    // Formatear día y mes con 2 dígitos
    const dia = String(fecha.getDate()).padStart(2, "0"); // 5 → "05"
    const mes = String(fecha.getMonth() + 1).padStart(2, "0"); // 1 → "01"
    const año = fecha.getFullYear();

    const fechaFormateada = `${dia}-${mes}-${año}`; // Formato: 05-01-2024

    fetch(
      `${UrlApi}/vehiculos_revision/revision_por_fecha/solo_observacion/${vehiculo.id_vehiculo}/${fechaFormateada}`
    )
      .then((response) => response.json())
      .then((data) => {
        setObservaciones(data);
        if (data && data.length > 0) {
          // Actualizar el estado con el primer uuid_revision
          setCurrentUuid(data[0].uuid_revision);
        }
      });
  };

  useEffect(() => {
    obtenerObservaciones();
  }, [vehiculo]);

  // ----------------------------------------------

  const handleOpenCloseAdj = () => {
    if (currentUuid) {
      setOpenCloseAdj(true);
    } else {
      console.error("UUID no encontrado:");
    }
  };

  // ----------------------------------------------

  // Agrupar observaciones por parte_vehiculo y contar Tiempo/KM
  const observacionesAgrupadas = useMemo(() => {
    return observaciones.reduce((grupos, obs) => {
      const categoria = obs.parte_vehiculo || "Sin categoría";
      if (!grupos[categoria]) {
        grupos[categoria] = { observaciones: [], tiempo: 0, km: 0, total: 0 };
      }

      grupos[categoria].observaciones.push(obs);

      // Contar observaciones de tiempo y kilometraje
      if (obs.frecuencia) grupos[categoria].tiempo += 1;
      if (obs.frecuencia_km) grupos[categoria].km += 1;

      // Calcular total (suma de tiempo y km)
      grupos[categoria].total = grupos[categoria].tiempo + grupos[categoria].km;

      return grupos;
    }, {});
  }, [observaciones]);

  return (
    <>
      <Box sx={{ bgcolor: "#F2F2F2", minHeight: "100vh" }}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>

            <Box
              sx={{
                ml: 2,
                flex: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Patente a la izquierda */}
              <Typography
                style={{
                  fontSize: isMobile ? "0.9rem" : "1.2rem",
                  fontWeight: "bold",
                }}
              >
                <strong>Patente:</strong> {vehiculo?.patente}
              </Typography>

              {/* Botón de adjuntar a la derecha */}
              <Button
                onClick={handleOpenCloseAdj}
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<AttachFileIcon />}
                style={{
                  fontSize: isMobile ? "0.7rem" : "0.87rem",
                  padding: isMobile ? "4px 8px" : "6px 16px",
                  minWidth: isMobile ? "80px" : "auto",
                }}
              >
                Adjuntos
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, marginBottom: "65px" }}>
          {Object.entries(observacionesAgrupadas).map(
            ([categoria, { observaciones, tiempo, km }]) => (
              <Accordion
                defaultExpanded
                key={categoria}
                sx={{
                  minHeight: 50,
                  padding: "0",
                  margin: "0",
                  width: "100%",
                  overflow: "hidden",
                  "&::before": {
                    display: "none", // Elimina el borde predeterminado
                  },
                  "&::before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  sx={{
                    bgcolor: "#1976d2",
                    color: "#fff",
                    padding: "5px 16px",
                    height: "30px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    "&:hover": {
                      bgcolor: "#125a9e",
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isMobile ? "0.85rem" : "1rem",
                      fontWeight: "bold",
                      flexGrow: 1,
                    }}
                  >
                    {categoria}
                  </Typography>

                  {/* Contadores de Tiempo y KM */}
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <Chip
                      variant="outlined"
                      label={`Tiempo: ${tiempo}`}
                      sx={{
                        bgcolor: "#2e7d32",
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      size="small"
                    />
                    <Chip
                      variant="outlined"
                      label={`KM: ${km}`}
                      sx={{
                        bgcolor: "#2e7d32",
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      size="small"
                    />
                    <Chip
                      variant="outlined"
                      label={`Total: ${tiempo + km}`}
                      sx={{
                        bgcolor: "#d32f2f",
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      size="small"
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={2}>
                    {observaciones.map((obs) => (
                      <Grid item xs={12} key={obs.uuid_revision}>
                        <Card
                          sx={{
                            width: "100%",
                            boxShadow: 3,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: 5,
                            },
                            transition: "transform 0.2s, box-shadow 0.2s",
                          }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {obs.tarea}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: obs.observacion
                                  ? "inherit"
                                  : "italic",
                                color: obs.observacion
                                  ? "text.primary"
                                  : "text.secondary",
                              }}
                            >
                              {obs.observacion}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>

        {/* Componente de adjuntos */}
        {openCloseAdj && (
          <FileUploadTareas
            fileId={currentUuid}
            openclose={true}
            mostrar={false}
            onClose={() => setOpenCloseAdj(false)}
          />
        )}

        <BottomDrawer vehiculo={vehiculo} />
      </Box>
    </>
  );
}

export default ObservacionesPanelMantenimiento;
